import {Mapper} from '@s-ui/domain'

import {AutoDealerStructuredDataSeoValueObject} from '../ValueObjects/AutoDealerStructuredDataSeoValueObject.js'

const EMPTY_PAGE = 0
const FIRST_PAGE = 1

export class SeoProfessionalStockMetaInformationMapper extends Mapper {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Object} deps.config
   * @param {GetUrlService} deps.getUrlService
   * @param {GetSearchPaginationUrlAdsService} deps.getSearchPaginationUrlAdsService
   * @param {SeoDataMapper} deps.seoDataMapper
   * @param {SeoLiteralMapper} deps.seoLiteralMapper
   * @param {Function} deps.seoDataValueObjectFactory
   * @param {Function} deps.searchRequestValueObjectFactory
   * */
  constructor({
    config,
    getSearchPaginationUrlAdsService,
    seoDataMapper,
    seoLiteralMapper,
    seoDataValueObjectFactory,
    searchRequestValueObjectFactory
  }) {
    super()
    this._config = config
    this._getSearchPaginationUrlAdsService = getSearchPaginationUrlAdsService
    this._seoDataMapper = seoDataMapper
    this._seoLiteralMapper = seoLiteralMapper
    this._seoDataValueObjectFactory = seoDataValueObjectFactory
    this._searchRequestValueObjectFactory = searchRequestValueObjectFactory

    const {QUESTION_MARK, PAGINATION_PATTERN, PROFESSIONAL_STOCK_RENTING_PAGE_NAME} = this._config.get('page')
    this._urlPaginationParam = `${QUESTION_MARK}${PAGINATION_PATTERN}`
    this._professionalStockRentingPageName = PROFESSIONAL_STOCK_RENTING_PAGE_NAME
    this._sectionPaths = this._config.get('COMMON').SECTION_PATHS
  }

  _getUrls = async ({dealerSlug, page, pageName, searchRequestObjectVO, section, totalPages}) => {
    const [error, urlVO] = await this._getSearchPaginationUrlAdsService.execute({
      searchRequestObjectVO,
      paginationPlaceholder: 'pageNumber',
      pageName,
      section,
      dealerSlug
    })

    if (error) return

    const isFirstPage = page === FIRST_PAGE || page === EMPTY_PAGE
    const protocol = this._config.get('URL_PROTOCOL')
    const host = this._config.get('HOST_DOMAIN')

    const [canonicalPathName] = urlVO.getUrl().split('?')

    const canonicalParam = isFirstPage ? '' : `${this._urlPaginationParam}${page}`

    const canonical = `${protocol}${host}${canonicalPathName}${canonicalParam}`

    return {
      canonical,
      next: this._getNextUrl({url: canonicalPathName, page, totalPages}),
      prev: this._getPrevUrl({url: canonicalPathName, page, totalPages})
    }
  }

  _getNextUrl = ({url, page, totalPages}) => {
    if (totalPages > page) {
      const nextPage = page + 1
      return `${url}${this._urlPaginationParam}${nextPage}`
    }
  }

  _getPrevUrl = ({url, page}) => {
    if (page > 1) {
      const prevPage = page - 1
      return `${url}${this._urlPaginationParam}${prevPage}`
    }
  }

  /**
   * @method
   */
  async map({culture, pageName, params = {}}) {
    const {dealer, dealerSlug, nResults, page = FIRST_PAGE, section, totalPages} = params

    const {professionalStock: professionalStockSharedConfig, professionalRentingStock: professionalRentingStockConfig} =
      this._config.get('seo')

    const professionalStockConfig =
      pageName === this._professionalStockRentingPageName
        ? professionalRentingStockConfig
        : professionalStockSharedConfig

    const {TITLE, META_DESCRIPTION, META_ROBOTS} = professionalStockConfig[culture][section || 'default']

    const placeholderValues = {
      name: dealer.name,
      province: dealer.location?.province,
      nResults,
      totalReviews: dealer.ratings?.totalReviews
    }

    const searchRequestObjectVO = this._searchRequestValueObjectFactory({page})
    const {canonical, next, prev} = await this._getUrls({
      dealerSlug,
      page,
      pageName,
      searchRequestObjectVO,
      section,
      totalPages
    })

    const parsedTitle = this._seoLiteralMapper.setText({text: TITLE}).setPlaceholderValues({placeholderValues}).map()

    const parsedDescription = this._seoLiteralMapper
      .setText({text: META_DESCRIPTION})
      .setPlaceholderValues({placeholderValues})
      .map()

    const dealerEntityStructuredDataVO = AutoDealerStructuredDataSeoValueObject.createWithDefaultValues({
      config: this._config,
      dealer
    })

    const dealerSchemaConfig = {
      [this._sectionPaths.RATINGS]: dealerEntityStructuredDataVO.baseSchemaWithRatings
    }

    const {links, metas, scripts, title} = this._seoDataMapper
      .title(parsedTitle)
      .description(parsedDescription)
      .robots(META_ROBOTS)
      .canonical(canonical)
      .addSchemaData(dealerSchemaConfig[section] || dealerEntityStructuredDataVO.baseSchema)
      .setPaginationLinks({next, prev})
      .setOpenGraphData({
        title: parsedTitle,
        description: parsedDescription,
        url: canonical
      })
      .setTwitterCardsData()
      .map()

    return this._seoDataValueObjectFactory({
      head: {
        link: links,
        meta: metas,
        scripts,
        title
      }
    })
  }
}
